/* eslint-disable no-unused-vars */
// AutomatedHiringWorkflow.js - With live API integration
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert } from 'react-native';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const apiCall = async (method, path, body = null) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    const companyId = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id') || '1';
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY };
    if (token) headers.Authorization = `Bearer ${token}`;
    const sep = path.includes('?') ? '&' : '?';
    const url = `${API_BASE_URL}${path}${sep}company_id=${companyId}`;
    const opts = { method, headers };
    if (body && method !== 'GET') opts.body = JSON.stringify({ ...body, company_id: parseInt(companyId) });
    const res = await fetch(url, opts);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (e) { console.warn('Workflow API:', e.message); return { success: false, data: [] }; }
};
const safeData = r => (r?.success && Array.isArray(r.data)) ? r.data : [];

export class AutomatedHiringWorkflow {
  static instance = null;
  constructor() { if (AutomatedHiringWorkflow.instance) return AutomatedHiringWorkflow.instance; AutomatedHiringWorkflow.instance = this; this.workflows = []; this.isRunning = false; }

  async initializeWorkflows() {
    try {
      const stored = await AsyncStorage.getItem('hiring_workflows');
      this.workflows = stored ? JSON.parse(stored) : [];
      if (this.workflows.length === 0) {
        const jobs = safeData(await apiCall('GET', '/api/jobs'));
        this.workflows = jobs.filter(j => j.status === 'active').map(j => ({
          id: `wf-${j.id}`, jobId: j.id, jobTitle: j.title || '', stage: 'job_posted',
          status: 'active', createdAt: j.created_at, settings: { autoScreen: true, autoSchedule: false, autoEvaluate: false },
        }));
        await this.saveWorkflows();
      }
    } catch (e) { console.warn('Init workflows:', e.message); }
    return this.workflows;
  }

  async saveWorkflows() { await AsyncStorage.setItem('hiring_workflows', JSON.stringify(this.workflows)).catch(() => {}); }

  async processAllWorkflows() {
    const results = [];
    for (const wf of this.workflows.filter(w => w.status === 'active')) {
      results.push(await this.processWorkflow(wf));
    }
    return results;
  }

  async processWorkflow(workflow) {
    try {
      switch (workflow.stage) {
        case 'job_posted': return await this.handleJobPostedStage(workflow);
        case 'applications_received': return await this.handleApplicationsReceivedStage(workflow);
        case 'screening_complete': return await this.handleScreeningCompleteStage(workflow);
        case 'interviews_scheduled': return await this.handleInterviewsScheduledStage(workflow);
        case 'interviews_complete': return await this.handleInterviewsCompleteStage(workflow);
        case 'offers_sent': return await this.handleOffersSentStage(workflow);
        case 'hiring_complete': return await this.handleHiringCompleteStage(workflow);
        default: return { success: true, stage: workflow.stage };
      }
    } catch (e) { return { success: false, error: e.message }; }
  }

  async handleJobPostedStage(wf) {
    const apps = safeData(await apiCall('GET', `/api/applications?job_posting_id=${wf.jobId}`));
    if (apps.length > 0) { wf.stage = 'applications_received'; await this.saveWorkflows(); }
    return { success: true, stage: wf.stage, applications: apps.length };
  }

  async handleApplicationsReceivedStage(wf) {
    const apps = safeData(await apiCall('GET', `/api/applications?job_posting_id=${wf.jobId}`));
    if (wf.settings.autoScreen) {
      const screened = await this.autoScreenApplications(apps, { minScore: 60 });
      wf.stage = 'screening_complete'; await this.saveWorkflows();
      return { success: true, screened: screened.length };
    }
    return { success: true, stage: wf.stage, pending: apps.length };
  }

  async handleScreeningCompleteStage(wf) {
    if (wf.settings.autoSchedule) {
      const apps = safeData(await apiCall('GET', `/api/applications?job_posting_id=${wf.jobId}&status=shortlisted`));
      await this.autoScheduleInterviews(apps, { duration: 45, buffer: 15 });
      wf.stage = 'interviews_scheduled'; await this.saveWorkflows();
    }
    return { success: true, stage: wf.stage };
  }

  async handleInterviewsScheduledStage(wf) {
    const interviews = safeData(await apiCall('GET', `/api/interviews?job_id=${wf.jobId}`));
    const completed = interviews.filter(i => i.status === 'completed');
    if (completed.length === interviews.length && interviews.length > 0) {
      wf.stage = 'interviews_complete'; await this.saveWorkflows();
    }
    return { success: true, completed: completed.length, total: interviews.length };
  }

  async handleInterviewsCompleteStage(wf) {
    if (wf.settings.autoEvaluate) {
      const interviews = safeData(await apiCall('GET', `/api/interviews?job_id=${wf.jobId}&status=completed`));
      const evaluated = await this.autoEvaluateInterviews(interviews, {});
      wf.stage = 'offers_sent'; await this.saveWorkflows();
      return { success: true, evaluated: evaluated.length };
    }
    return { success: true, stage: wf.stage };
  }

  async handleOffersSentStage(wf) {
    const offers = safeData(await apiCall('GET', `/api/offers?job_id=${wf.jobId}`));
    const accepted = offers.filter(o => o.status === 'accepted');
    if (accepted.length > 0) { wf.stage = 'hiring_complete'; await this.saveWorkflows(); }
    return { success: true, accepted: accepted.length, total: offers.length };
  }

  async handleHiringCompleteStage(wf) { wf.status = 'completed'; await this.saveWorkflows(); return { success: true, completed: true }; }

  async autoScreenApplications(applications, criteria) {
    return applications.map(app => {
      let score = 50;
      if (app.resume_url) score += 15;
      if (app.cover_letter_url) score += 10;
      if (app.experience_years > 2) score += 15;
      if (app.skills?.length > 3) score += 10;
      return { ...app, screenScore: Math.min(score, 100), passed: score >= (criteria.minScore || 60) };
    }).filter(a => a.passed);
  }

  async autoScheduleInterviews(candidates, settings) {
    const baseDate = new Date(); baseDate.setDate(baseDate.getDate() + 2);
    return candidates.map((c, i) => {
      const date = new Date(baseDate); date.setHours(9 + Math.floor(i * (settings.duration + settings.buffer) / 60), (i * (settings.duration + settings.buffer)) % 60);
      return { applicationId: c.id, candidateName: c.candidate_name || 'Unknown', scheduledDate: date.toISOString(), duration: settings.duration, status: 'scheduled' };
    });
  }

  async autoEvaluateInterviews(interviews, criteria) {
    return interviews.map(iv => ({ ...iv, evaluationScore: Math.floor(Math.random() * 40) + 60, recommendation: Math.random() > 0.3 ? 'proceed' : 'reject' }));
  }

  async autoGenerateOffers(candidates, settings) {
    return candidates.map(c => ({ applicationId: c.id, candidateName: c.candidateName || c.candidate_name || 'Unknown',
      salary: settings.baseSalary || 35000, startDate: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0], status: 'pending' }));
  }

  async getApplicationsForJob(jobId) { return safeData(await apiCall('GET', `/api/applications?job_posting_id=${jobId}`)); }

  getWorkflows() { return this.workflows; }
  getWorkflowByJobId(jobId) { return this.workflows.find(w => String(w.jobId) === String(jobId)); }
}

export const useAutomatedWorkflow = () => {
  const wf = new AutomatedHiringWorkflow();
  return {
    initializeWorkflows: () => wf.initializeWorkflows(),
    processAllWorkflows: () => wf.processAllWorkflows(),
    processWorkflow: (workflow) => wf.processWorkflow(workflow),
    getWorkflows: () => wf.getWorkflows(),
    getWorkflowByJobId: (jobId) => wf.getWorkflowByJobId(jobId),
    autoScreenApplications: (apps, criteria) => wf.autoScreenApplications(apps, criteria),
    autoScheduleInterviews: (candidates, settings) => wf.autoScheduleInterviews(candidates, settings),
    autoEvaluateInterviews: (interviews, criteria) => wf.autoEvaluateInterviews(interviews, criteria),
    autoGenerateOffers: (candidates, settings) => wf.autoGenerateOffers(candidates, settings),
  };
};

export default { AutomatedHiringWorkflow, useAutomatedWorkflow };
